import React, { useState } from "react";
import {
  Box,
  Card,
  Grid,
  Stack,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import {
  useGetBeauticianProductsQuery,
  useAddToCartMutation,
  useGetCartQuery,
} from "slices/eCommerce/shopProducts";
import { useSubmitCartMutation } from "slices/beautyApp/beauticianSlice";
import { useGetAddOnStockQuery } from "slices/beautyApp/addOnStockSlice";
import { IMAGE_URL } from "network";
import { useGetSingleBeauticianQuery } from "slices/beautyApp/beauticianSlice";

export default function Inventory() {
  const { beauticianId } = useParams();
  const navigate = useNavigate();

  const {
    data: beautician,
    isSuccess,
    refetch,
  } = useGetSingleBeauticianQuery(beauticianId);
  const b = beautician?.data;
  /* -------------------- STATE -------------------- */
  const [activeTab, setActiveTab] = useState(0);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantities, setQuantities] = useState({});
  const [stockQuantities, setStockQuantities] = useState({});

  /* -------------------- API -------------------- */
  const { data: productsData } = useGetBeauticianProductsQuery();
  const [addToCart] = useAddToCartMutation();
  const [submitCart] = useSubmitCartMutation();

  const { data: cartData, refetch: refetchCart } = useGetCartQuery({
    userId: beauticianId,
  });

  const { data: stockData } = useGetAddOnStockQuery({});

  /* -------------------- HANDLERS -------------------- */
  const handleTabChange = (_, newValue) => setActiveTab(newValue);

  /* -------- Beautician Products -------- */
  const hitProductApi = async (variationId, quantity, count) => {
    if (!selectedProduct) return;

    const payload = {
      category: "e-commerce",
      userId: beauticianId,
      quantity,
      itemRef: selectedProduct._id,
      itemType: "shop_product",
      variationId,
      uses_count: count,
    };

    await addToCart(payload).unwrap();
    refetchCart();
  };

  const handleAdd = (variationId, count) => {
    setQuantities((prev) => {
      const newQty = (prev[variationId] || 0) + 1;
      hitProductApi(variationId, newQty, count);
      return { ...prev, [variationId]: newQty };
    });
  };

  const handleRemove = (variationId, count) => {
    setQuantities((prev) => {
      const currentQty = prev[variationId] || 0;
      if (currentQty <= 0) return prev;

      const newQty = currentQty - 1;
      if (newQty > 0) hitProductApi(variationId, newQty, count);

      return { ...prev, [variationId]: newQty };
    });
  };

  /* -------- Stock Items -------- */
  const hitStockApi = async (itemId, quantity) => {
    const payload = {
      category: "e-commerce",
      userId: beauticianId,
      quantity,
      itemRef: itemId,
      itemType: "addonstocks",
    };

    await addToCart(payload).unwrap();
    refetchCart();
  };

  const handleAddStock = (item) => {
    setStockQuantities((prev) => {
      const currentQty = prev[item._id] || 0;
      if (currentQty >= item.remainingCount) return prev;

      const newQty = currentQty + 1;
      hitStockApi(item._id, newQty);

      return { ...prev, [item._id]: newQty };
    });
  };

  const handleRemoveStock = (item) => {
    setStockQuantities((prev) => {
      const currentQty = prev[item._id] || 0;
      if (currentQty <= 0) return prev;

      const newQty = currentQty - 1;
      if (newQty > 0) hitStockApi(item._id, newQty);

      return { ...prev, [item._id]: newQty };
    });
  };

  const handleSubmitCart = async () => {
    await submitCart(beauticianId).unwrap();
    refetchCart();
  };

  /* -------------------- RENDER -------------------- */
  return (
    <DashboardLayout>
      <DashboardNavbar />

      <Box px={3} pt={2}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/beauty-app/beauticians/beautician/${beauticianId}`)}
          sx={{ mb: 2, color: "black", border: "1px solid black" }}
        >
          Back
        </Button>
      </Box>

      <Grid container spacing={3} mb={4}>
        {/* Profile Details */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
              height: "100%",
            }}
          >
            {/* <EditIcon
              color="primary"
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                cursor: "pointer",
              }}
              onClick={() => setProfile(true)}
            /> */}
            <Typography variant="h6" fontWeight={600} mb={2}>
              Profile Details
            </Typography>

            <Stack spacing={1}>
              <Typography variant="body2">
                <strong>Name:</strong> {b?.user?.name || "N/A"}
              </Typography>

              <Typography variant="body2">
                <strong>Email:</strong> {b?.user?.email || "N/A"}
              </Typography>

              <Typography variant="body2">
                <strong>Phone:</strong> {b?.user?.mobile || "N/A"}
              </Typography>

              <Typography variant="body2">
                <strong>Status:</strong> {b?.status ? "Active" : "Inactive"}
              </Typography>
            </Stack>
          </Card>
        </Grid>

        {/* Service Completed Section */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              p: 3,
              borderRadius: 3,
              boxShadow: "0 2px 10px rgba(0,0,0,0.06)",
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={3}>
              Service Performance
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography variant="h5" fontWeight={600} color="primary">
                    {b?.orderStats?.totalOrders || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Bookings
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    color="warning.main"
                  >
                    {b?.orderStats?.uncompletedOrders || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Services
                  </Typography>
                </Card>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Card
                  sx={{
                    p: 2,
                    textAlign: "center",
                    borderRadius: 2,
                    backgroundColor: "#fafafa",
                  }}
                >
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    color="success.main"
                  >
                    {b?.orderStats?.completedOrders || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Services Completed
                  </Typography>
                </Card>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>

      {/* TABS */}
      <Box px={3} pt={2}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Beautician Products" />
          <Tab label="Stock Items" />
        </Tabs>
      </Box>

      <Box p={3} display="flex" gap={3}>
        {/* LEFT */}
        <Box flex={3}>
          {/* PRODUCTS */}
          {activeTab === 0 && (
            <>
              <Typography variant="h5" fontWeight={600} mb={3}>
                Beautician Products
              </Typography>

              <Grid container spacing={2}>
                {productsData?.data?.map((product) => (
                  <Grid item xs={12} sm={6} md={4} key={product._id}>
                    <Card
                      onClick={() => setSelectedProduct(product)}
                      sx={{ cursor: "pointer", borderRadius: 3 }}
                    >
                      <Box
                        component="img"
                        crossOrigin="anonymous"
                        src={IMAGE_URL + product.thumbnail}
                        sx={{ width: "100%", height: 100, objectFit: "cover" }}
                      />
                      <Box p={2}>
                        <Typography fontWeight={600} noWrap>
                          {product.name}
                        </Typography>
                        <Typography variant="body2">
                          {product.variations?.length || 0} Variations
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {/* STOCK */}
          {activeTab === 1 && (
            <>
              <Typography variant="h5" fontWeight={600} mb={3}>
                Stock Items
              </Typography>

              <Grid container spacing={2}>
                {stockData?.data?.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item._id}>
                    <Card sx={{ borderRadius: 3 }}>
                      <Box
                        component="img"
                        crossOrigin="anonymous"
                        src={IMAGE_URL + item.image}
                        sx={{ width: "100%", height: 100, objectFit: "cover" }}
                      />

                      <Box p={2}>
                        <Typography fontWeight={600}>{item.name}</Typography>
                        <Stack direction="row" spacing={2} mt={1}>
                          <Typography>Total: {item.totalCount}</Typography>
                          <Typography
                            fontWeight={600}
                            color={
                              item.remainingCount > 0
                                ? "success.main"
                                : "error.main"
                            }
                          >
                            Available: {item.remainingCount}
                          </Typography>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={1}
                          mt={2}
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          {/* <IconButton
                            size="small"
                            disabled={(stockQuantities[item._id] || 0) <= 0}
                            onClick={() => handleRemoveStock(item)}
                          >
                            <RemoveIcon />
                          </IconButton> */}

                          <Typography fontWeight={600}>
                            {stockQuantities[item._id] || 0}
                          </Typography>

                          <IconButton
                            size="small"
                            disabled={
                              item.remainingCount <= 0 ||
                              (stockQuantities[item._id] || 0) >=
                                item.remainingCount
                            }
                            onClick={() => handleAddStock(item)}
                          >
                            <AddIcon />
                          </IconButton>
                        </Stack>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>

        {/* CART */}
        <Box flex={1} p={2} border="1px solid #ddd" borderRadius={2}>
          <Typography variant="h6" mb={2}>
            Cart Items
          </Typography>

          {!cartData?.items?.length ? (
            <Typography>No items in cart</Typography>
          ) : (
            cartData.items.map((item) => (
              <Card key={item._id} sx={{ p: 1, mb: 1 }}>
                <Typography fontWeight={600}>{item?.itemRef?.name}</Typography>

                <Typography fontWeight={400}>
                  {item.itemType === "shop_product"
                    ? ` Price: ₹${
                        (item?.itemRef?.variations?.find(
                          (v) => v._id === item.variation
                        )?.price || 0) * item.quantity
                      }`
                    : `Price: ₹${item.quantity * (item?.itemRef?.price || 0)}`}
                </Typography>
              </Card>
            ))
          )}

          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 2 }}
            disabled={!cartData?.items?.length}
            onClick={handleSubmitCart}
          >
            Submit Cart
          </Button>
        </Box>
      </Box>

      {/* VARIATION DIALOG */}
      <Dialog
        open={!!selectedProduct}
        onClose={() => setSelectedProduct(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Select Variations</DialogTitle>
        <DialogContent>
          {selectedProduct?.variations?.map((variation) => (
            <Card key={variation._id} sx={{ p: 2, mb: 2 }}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography fontWeight={600}>{selectedProduct.name}</Typography>
                <Typography fontWeight={600}>₹{variation.price}</Typography>

                {/* <IconButton
                  onClick={() =>
                    handleRemove(variation._id, variation.uses_count)
                  }
                  sx={{
                    mt: 2,
                    color: "black !important ",
                    border: "1px solid black",
                  }}
                >
                  <RemoveIcon />
                </IconButton> */}

                <Typography>{quantities[variation._id] || 0}</Typography>

                <IconButton
                  onClick={() => handleAdd(variation._id, variation.uses_count)}
                  sx={{
                    mt: 2,
                    color: "black !important ",
                    border: "1px solid black",
                  }}
                >
                  <AddIcon />
                </IconButton>
              </Stack>
            </Card>
          ))}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setSelectedProduct(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
